import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDevices, useSetServiceDevices, useUpdateProject } from '../api/queries'
import type { Project } from '../api/types'
import { formatDate } from '../lib/format'
import { Card } from './ui'

/** Samme lyseblå som servicebesøgene i kalenderen (beslutning 028). */
export const servicePillStyle = { background: '#E0F2FE', color: '#075985' }

/**
 * Sagens type - Sag eller Service (beslutning 076).
 *
 * Den, der må rette sagen, kan skifte typen her. Alle andre ser kun et mærke på
 * servicesager; en almindelig sag får intet mærke, fordi det er det normale.
 */
export function ProjectKindControl({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const update = useUpdateProject(project.id)
  const [error, setError] = useState<string | null>(null)

  if (!canEdit) {
    return project.kind === 'service' ? (
      <span className="pill" style={servicePillStyle}>
        Service
      </span>
    ) : null
  }

  async function change(kind: string) {
    setError(null)
    try {
      await update.mutateAsync({ kind })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Typen kunne ikke skiftes.')
    }
  }

  return (
    <span className="row" style={{ gap: 6 }}>
      <select
        aria-label="Sagens type"
        value={project.kind}
        disabled={update.isPending}
        onChange={(e) => void change(e.target.value)}
        style={{ width: 'auto' }}
      >
        <option value="project">Sag</option>
        <option value="service">Service</option>
      </select>
      {error && <span className="tiny warn">{error}</span>}
    </span>
  )
}

/**
 * Enhederne, servicesagen handler om (beslutning 076).
 *
 * Enhederne hører til kunden (beslutning 024) og vælges blandt dem, der står hos
 * sagens kunde. Selve servicen - checkliste, fotos og underskrift - startes på
 * enheden, så historikken bliver hos den, også når sagen er lukket.
 */
export function ServiceDevicesCard({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const hasCustomer = Boolean(project.customerId)
  const { data: linked } = useDevices({ serviceProjectId: project.id, includeInactive: true })
  const { data: customerDevices } = useDevices(
    { customerId: project.customerId ?? undefined, includeInactive: true },
    hasCustomer
  )
  const save = useSetServiceDevices(project.id)
  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)

  const devices = linked ?? []
  const available = hasCustomer ? customerDevices ?? [] : []

  function start() {
    setChosen(devices.map((device) => device.id))
    setError(null)
    setEditing(true)
  }

  function toggle(id: number) {
    setChosen((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  async function submit() {
    setError(null)
    try {
      await save.mutateAsync(chosen)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enhederne kunne ikke gemmes.')
    }
  }

  return (
    <Card
      title={`Enheder på servicesagen${devices.length ? ` (${devices.length})` : ''}`}
      actions={
        canEdit && hasCustomer && !editing ? (
          <button className="btn btn--sm" onClick={start}>
            Vælg enheder
          </button>
        ) : undefined
      }
    >
      {!hasCustomer ? (
        <div className="small muted">
          Sagen har ingen kunde. Vælg kunden først - enhederne hører til kunden.
        </div>
      ) : editing ? (
        <div className="stack">
          {available.length === 0 ? (
            <div className="small muted">
              Der står ingen enheder hos {project.customer}. Opret dem under <Link to="/devices">Enheder</Link>.
            </div>
          ) : (
            available.map((device) => {
              const checked = chosen.includes(device.id)
              return (
                <label key={device.id} className="row" style={{ gap: 10, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={checked}
                    // En udtjent enhed kan fjernes, men ikke sættes på.
                    disabled={!device.isActive && !checked}
                    onChange={() => toggle(device.id)}
                  />
                  <span className="small" style={{ minWidth: 0 }}>
                    <strong>{device.name}</strong> <span className="tiny muted">{device.code}</span>
                    {!device.isActive && <span className="tiny muted"> - udtjent</span>}
                    {device.location && <div className="tiny muted">{device.location}</div>}
                  </span>
                </label>
              )
            })
          )}

          {error && <div className="error-box">{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary btn--sm" disabled={save.isPending} onClick={() => void submit()}>
              Gem
            </button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : devices.length === 0 ? (
        <div className="small muted">
          Ingen enheder valgt endnu.
          {canEdit ? ' Tryk Vælg enheder og sæt flueben ved det, servicen gælder.' : ''}
        </div>
      ) : (
        <div className="stack">
          {devices.map((device) => (
            <Link key={device.id} className="row row--between device-row" to={`/devices/${device.id}`}>
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8 }}>
                  <strong className="small">{device.name}</strong>
                  <span className="tiny muted device-row__code">{device.code}</span>
                </div>
                <div className="tiny muted">{device.location || 'Ingen placering angivet'}</div>
              </div>
              <div className="tiny muted" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                {device.lastServiceDate ? `Sidst ${formatDate(device.lastServiceDate)}` : 'Aldrig serviceret'}
                <div>
                  {device.serviceIntervalMonths ? `Næste ${formatDate(device.nextServiceDate)}` : 'Ingen serviceaftale'}
                </div>
              </div>
            </Link>
          ))}
          <p className="tiny muted" style={{ margin: 0 }}>
            Tryk på en enhed og vælg <strong>Start service</strong>. Checkliste, fotos og underskrift gemmes på
            enheden, så historikken bliver, når sagen er lukket.
          </p>
        </div>
      )}
    </Card>
  )
}
